import { HTTPMethod } from 'utils/HTTPUtils';
import { authenticationHeaders } from 'networking/mindfeed/utils/authenticationHeaders';
import { routes } from 'networking/mindfeed/constants/api';
import { apiFetch } from 'networking/mindfeed/utils/apiClient';
import { Comment, CommentId, CommentSubject } from 'models/Comment';

export type CommentPayload = {
  content: string;
};

export type CreateCommentPayload = CommentPayload & {
  parentId?: CommentId;
};

export type UpdateCommentPayload = CommentPayload;

const commentsRoute = ({ type, id }: CommentSubject): string => {
  switch (type) {
    case 'memo':
      return routes.memos.comments(id);
    case 'media':
      return routes.media.comments(id);
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unhandled comment subject type: ${_exhaustive}`);
    }
  }
};

export const getComments = async (
  subject: CommentSubject,
): Promise<Comment[]> => {
  const response = await apiFetch<{ data: Comment[] }>(commentsRoute(subject), {
    method: HTTPMethod.GET,
    headers: await authenticationHeaders(),
  });
  return response?.data ?? [];
};

export const getComment = async (commentId: CommentId): Promise<Comment> =>
  apiFetch<Comment>(routes.comments.byId(commentId), {
    method: HTTPMethod.GET,
    headers: await authenticationHeaders(),
  });

export const createComment = async (
  subject: CommentSubject,
  payload: CreateCommentPayload,
): Promise<Comment> =>
  apiFetch<Comment>(commentsRoute(subject), {
    method: HTTPMethod.POST,
    headers: await authenticationHeaders(),
    body: JSON.stringify(payload),
  });

export const updateComment = async (
  commentId: CommentId,
  payload: UpdateCommentPayload,
): Promise<Comment> =>
  apiFetch<Comment>(routes.comments.byId(commentId), {
    method: HTTPMethod.PUT,
    headers: await authenticationHeaders(),
    body: JSON.stringify(payload),
  });

export const deleteComment = async (commentId: CommentId): Promise<void> =>
  apiFetch<void>(routes.comments.byId(commentId), {
    method: HTTPMethod.DELETE,
    headers: await authenticationHeaders(),
  });
